import React from 'react';
import { Form, Button, Row, Col, Card, InputGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { CLIENT_STATUSES, DEFAULT_CLIENT, DEFAULT_TAGS } from '../../utils/clientModel';
import { Badge } from 'react-bootstrap';
import { FaUser, FaBuilding, FaEnvelope, FaPhone, FaMapMarkerAlt, FaPhoneAlt, FaTag, FaCalendarAlt, FaCalendarCheck, FaCommentAlt, FaSave } from 'react-icons/fa';

// Validation schema using Yup
const clientSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string(),
  email: Yup.string().email('Invalid email address'),
  phone: Yup.string().matches(/^\+?[\d\s()-]{10,15}$/, 'Invalid phone number'),
  alternatePhone: Yup.string().matches(/^\+?[\d\s()-]{10,15}$/, 'Invalid phone number').nullable(),
  status: Yup.string().required('Status is required'),
});

const ClientForm = ({ client = DEFAULT_CLIENT, onSubmit, isLoading }) => {
  return (
    <Card className="client-form-card shadow-sm">
      <Card.Header className="bg-transparent border-0 pt-4 px-4">
        <h4 className="section-title mb-0">Client <span className="highlight-text">Information</span></h4>
      </Card.Header>
      <Card.Body className="p-4">
        <Formik
          initialValues={client}
          validationSchema={clientSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label">First Name*</Form.Label>
                    <InputGroup className="filter-input">
                      <InputGroup.Text>
                        <FaUser />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="firstName"
                        className="search-control"
                        placeholder="Enter first name"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.firstName && errors.firstName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.firstName}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label">Last Name</Form.Label>
                    <InputGroup className="filter-input">
                      <InputGroup.Text>
                        <FaUser />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="lastName"
                        className="search-control"
                        placeholder="Enter last name"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.lastName && errors.lastName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.lastName}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label">Company</Form.Label>
                    <InputGroup className="filter-input">
                      <InputGroup.Text>
                        <FaBuilding />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="company"
                        className="search-control"
                        placeholder="Enter company name"
                        value={values.company}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label">Status*</Form.Label>
                    <Form.Select
                      name="status"
                      value={values.status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.status && errors.status}
                      className="form-select-custom"
                    >
                      {Object.values(CLIENT_STATUSES).map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.status}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label">Email</Form.Label>
                    <InputGroup className="filter-input">
                      <InputGroup.Text>
                        <FaEnvelope />
                      </InputGroup.Text>
                      <Form.Control
                        type="email"
                        name="email"
                        className="search-control"
                        placeholder="Enter email address"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label">Phone</Form.Label>
                    <InputGroup className="filter-input">
                      <InputGroup.Text>
                        <FaPhone />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="phone"
                        className="search-control"
                        placeholder="Enter primary phone number"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.phone && errors.phone}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.phone}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label">Address</Form.Label>
                    <InputGroup className="filter-input">
                      <InputGroup.Text>
                        <FaMapMarkerAlt />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="address"
                        className="search-control"
                        value={values.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Full address including city, state, zip"
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label">Alternate Phone</Form.Label>
                    <InputGroup className="filter-input">
                      <InputGroup.Text>
                        <FaPhoneAlt />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="alternatePhone"
                        className="search-control"
                        value={values.alternatePhone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.alternatePhone && errors.alternatePhone}
                        placeholder="Alternate contact number"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.alternatePhone}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label d-flex align-items-center">
                      <FaTag className="me-2" /> Tags
                    </Form.Label>
                    <div className="tags-selector">
                      {DEFAULT_TAGS.map(tag => {
                        const isSelected = values.tags?.includes(tag);
                        return (
                          <Badge 
                            key={tag} 
                            bg={isSelected ? 'primary' : 'light'}
                            text={isSelected ? 'white' : 'dark'}
                            className={`tag-badge me-2 mb-2 ${isSelected ? 'selected-tag' : ''}`}
                            onClick={() => {
                              const currentTags = values.tags ? values.tags.split(',') : [];
                              let newTags;
                              if (currentTags.includes(tag)) {
                                newTags = currentTags.filter(t => t !== tag);
                              } else {
                                newTags = [...currentTags, tag];
                              }
                              handleChange({ target: { name: 'tags', value: newTags.join(',') } });
                            }}
                          >
                            <span className="tag-icon">
                              <FaTag />
                            </span>
                            {tag}
                          </Badge>
                        );
                      })}
                    </div>
                    <InputGroup className="filter-input mt-3">
                      <InputGroup.Text>
                        <FaTag />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="tags"
                        className="search-control"
                        value={values.tags}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Custom tags (comma-separated)"
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label d-flex align-items-center">
                      <FaCalendarAlt className="me-2" /> First Contact Date/Time
                    </Form.Label>
                    <InputGroup className="filter-input">
                      <InputGroup.Text>
                        <FaCalendarAlt />
                      </InputGroup.Text>
                      <Form.Control
                        type="datetime-local"
                        name="firstContactDate"
                        className="search-control date-input"
                        value={values.firstContactDate ? values.firstContactDate.split('.')[0] : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="filter-label d-flex align-items-center">
                      <FaCalendarCheck className="me-2" /> Next Follow-up Date/Time
                    </Form.Label>
                    <InputGroup className="filter-input">
                      <InputGroup.Text>
                        <FaCalendarCheck />
                      </InputGroup.Text>
                      <Form.Control
                        type="datetime-local"
                        name="nextFollowUp"
                        className="search-control date-input"
                        value={values.nextFollowUp ? values.nextFollowUp.split('.')[0] : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-4">
                <Form.Label className="filter-label d-flex align-items-center">
                  <FaCommentAlt className="me-2" /> Remarks
                </Form.Label>
                <InputGroup className="filter-input">
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="remarks"
                    className="remarks-textarea"
                    value={values.remarks}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Important information about the client"
                  />
                </InputGroup>
              </Form.Group>

              <div className="d-flex justify-content-end mt-5">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="btn-glow px-4 py-2"
                >
                  <span className="btn-text-wrapper d-flex align-items-center">
                    <FaSave className="me-2" />
                    {isLoading ? 'Saving...' : 'Save Client'}
                  </span>
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default ClientForm;
